import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import {
  ArrowRightIcon, CheckIcon, SparkleIcon,
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  MealIcon, DocumentIcon, ReceiptIcon, ChartIcon,
  ClockIcon, GrowthIcon, ZapIcon, HeartIcon,
} from "@/components/ui/TriIcons";
import { products, getProductById, type ProductDetail } from "@/lib/product-data";
import { InteractiveDemo, IdentityDemo, LearnDemo, TransitDemo, ConnectDemo, CampusDemo, IntelligenceDemo, GovernmentDemo, PayDemo } from "@/components/landing/interactive-demo";

const EASE = [0.4, 0, 0.2, 1] as const;
const TEAL = "#7B1416";
const FOREST = "#1C1917";
const INK = "#1C1917";

export function ProductDetailPage() {
  const { productId } = useParams({ from: "/ecosystem/$productId" });
  const product = getProductById(productId);
  const navigate = useNavigate();

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5EFE4]">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1C1917]">Product not found</h1>
          <Button asChild className="mt-6">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const idx = products.findIndex((p) => p.id === productId);
  const nextProduct = products[(idx + 1) % products.length];
  const prevProduct = products[(idx - 1 + products.length) % products.length];
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F5EFE4]">
      <Navbar />
      <ProductHero product={product} onDemo={() => setDemoOpen(true)} />
      <ProductCapabilities product={product} />
      <ProductVisualShowcase product={product} onDemo={() => setDemoOpen(true)} />
      <ProductEcosystemRole product={product} />
      <ProductStats product={product} />
      <ProductNavigation prev={prevProduct} next={nextProduct} onNavigate={(id) => navigate({ to: "/ecosystem/$productId", params: { productId: id } })} />
      <Footer />
      <AnimatePresence>
        {demoOpen && <InteractiveDemo productId={product.id} onClose={() => setDemoOpen(false)} />}
      </AnimatePresence>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════
function ProductHero({ product, onDemo }: { product: ProductDetail; onDemo: () => void }) {
  const Icon = product.icon;
  return (
    <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute rounded-full"
          style={{
            left: "10%", top: "20%", width: 500, height: 500,
            background: `radial-gradient(circle, ${product.color}10 0%, transparent 65%)`,
            filter: "blur(60px)",
          }}
          animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute rounded-full"
          style={{
            right: "5%", bottom: "10%", width: 400, height: 400,
            background: `radial-gradient(circle, ${product.color}08 0%, transparent 65%)`,
            filter: "blur(50px)",
          }}
          animate={{ x: [0, -30, 0], y: [0, 20, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-6 sm:px-8">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 flex items-center gap-2 text-sm"
        >
          <Link to="/" className="text-[#999] transition hover:text-[#1C1917]">Home</Link>
          <span className="text-[#ccc]">/</span>
          <span style={{ color: product.color }}>{product.shortName}</span>
        </motion.div>

        {/* Category badge */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="mb-6 flex items-center gap-4"
        >
          <div className="h-px w-12" style={{ background: product.color }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: product.color }}>
            {product.category}
          </span>
        </motion.div>

        {/* Icon + Name */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.5, ease: EASE }}
          className="mb-8 flex items-center gap-5"
        >
          <div
            className="grid h-20 w-20 shrink-0 place-items-center rounded-3xl shadow-lg"
            style={{ background: product.gradient }}
          >
            <Icon className="h-10 w-10" accent="#fff" style={{ color: "#fff" }} />
          </div>
          <h1 className="font-display text-4xl font-medium tracking-tight text-[#1C1917] sm:text-5xl lg:text-6xl">
            {product.name}
          </h1>
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="font-display text-2xl font-medium leading-[1.3] sm:text-3xl"
          style={{ color: product.color }}
        >
          {product.tagline}
        </motion.p>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="mt-6 max-w-3xl text-lg leading-[1.7] text-[#1C1917]/80"
        >
          {product.description}
        </motion.p>

        {/* Unique badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.5 }}
          className="mt-8 flex items-start gap-3 rounded-2xl border p-5 max-w-3xl"
          style={{ borderColor: `${product.color}20`, background: `${product.color}05` }}
        >
          <SparkleIcon className="mt-0.5 h-5 w-5 shrink-0" accent={product.color} style={{ color: product.color }} />
          <p className="text-sm leading-relaxed text-[#1C1917]/80">
            <span className="font-semibold" style={{ color: product.color }}>Unique to this market: </span>
            {product.unique}
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row"
        >
          <Button asChild size="lg" className="h-12 rounded-full px-7 shadow-lg" style={{ background: product.color, color: "#fff", border: "none" }}>
            <Link to="/auth">Get Started <ArrowRightIcon className="ml-2 h-4 w-4" accent="#fff" style={{ color: "#fff" }} /></Link>
          </Button>
          <Button variant="outline" size="lg" className="h-12 rounded-full px-6" onClick={onDemo}>
            Try Interactive Demo
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// CAPABILITIES
// ════════════════════════════════════════════════════════════
function ProductCapabilities({ product }: { product: ProductDetail }) {
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    setVisibleCount(6);
  }, [product.id]);

  return (
    <section className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: product.color }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: product.color }}>
            Core Capabilities
          </span>
        </div>
        <h2 className="font-display text-3xl font-medium tracking-tight text-[#1C1917] sm:text-4xl text-balance">
          {product.capabilities.length} capabilities. One platform.
        </h2>

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {product.capabilities.slice(0, visibleCount).map((cap, i) => (
              <motion.div
                key={`${product.id}-${cap}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: i * 0.03, duration: 0.3 }}
                className="group flex items-center gap-3 rounded-xl border bg-white p-4 transition-all hover:shadow-md"
                style={{ borderColor: "rgba(26,26,26,0.06)" }}
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-all" style={{ background: `${product.color}12` }}>
                  <CheckIcon className="h-4 w-4" accent={product.color} style={{ color: product.color }} />
                </div>
                <span className="text-sm font-medium text-[#1C1917]">{cap}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {product.capabilities.length > visibleCount && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setVisibleCount(product.capabilities.length)}
            className="mt-6 flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-all hover:shadow-sm"
            style={{ borderColor: `${product.color}30`, color: product.color }}
          >
            Show all {product.capabilities.length} capabilities
            <ArrowRightIcon className="h-4 w-4" accent={product.color} style={{ color: product.color }} />
          </motion.button>
        )}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// VISUAL SHOWCASE — unique per product
// ════════════════════════════════════════════════════════════
function ProductVisualShowcase({ product, onDemo }: { product: ProductDetail; onDemo: () => void }) {
  return (
    <section className="relative py-20 sm:py-28" style={{ background: "#EDF5F2" }}>
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: product.color }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: product.color }}>
            Live Interactive Showcase
          </span>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-display text-3xl font-medium tracking-tight text-[#1C1917] sm:text-4xl text-balance">
            See it in action.
          </h2>
          <Button variant="outline" size="lg" className="h-11 rounded-full px-5" onClick={onDemo}>
            Open Full Demo <ArrowRightIcon className="ml-2 h-4 w-4" accent={product.color} style={{ color: product.color }} />
          </Button>
        </div>
        <p className="mt-3 max-w-2xl text-base leading-[1.7] text-[#1C1917]/80">
          Explore {product.shortName} interactively — click tabs, trigger actions, and experience the real product workflow.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 overflow-hidden rounded-3xl border bg-white shadow-xl"
          style={{ borderColor: `${product.color}15` }}
        >
          <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${product.color}, ${product.color}66)` }} />
          <InlineInteractiveDemo productId={product.id} />
        </motion.div>
      </div>
    </section>
  );
}

// Inline wrapper that renders the interactive demo content (non-modal)
function InlineInteractiveDemo({ productId }: { productId: string }) {
  const [tab, setTab] = useState(0);
  const product = getProductById(productId);
  if (!product) return null;
  const tabs = inlineTabs[product.id] ?? ["Overview"];

  return (
    <div className="flex flex-col">
      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b px-6 py-3" style={{ borderColor: `${product.color}10` }}>
        {tabs.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition ${tab === i ? "text-white" : "text-[#888] hover:text-[#1C1917]"}`}
            style={tab === i ? { background: product.color } : {}}
          >
            {t}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="p-8 sm:p-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${product.id}-${tab}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <InlineDemoContent productId={product.id} tab={tab} product={product} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

const inlineTabs: Record<string, string[]> = {
  identity: ["ID Card", "Biometric", "Permissions", "QR Scan"],
  learn: ["Gradebook", "AI Tutor", "Timetable", "Assignments"],
  transit: ["Live Map", "Route Planner", "Boarding Log", "Alerts"],
  connect: ["Message Stream", "Broadcast", "Translation", "Analytics"],
  campus: ["Living Campus", "Student Journey", "Cafeteria", "Boarding", "Security", "Intelligence"],
  intelligence: ["AI Query", "Predictions", "Risk Radar", "Reports"],
  government: ["Compliance", "EMIS Report", "Audit Trail", "Risk Score"],
  pay: ["Payment Flow", "Sub-Wallets", "Reconciliation", "Collections AI"],
};

// Delegates to the same demo components used in the modal
function InlineDemoContent({ productId, tab, product }: { productId: string; tab: number; product: ProductDetail }) {
  // Re-import the demo components from interactive-demo
  // We use a mapping to render the right component
  const DemoComponent = demoComponentMap[productId];
  if (!DemoComponent) return null;
  return <DemoComponent tab={tab} product={product} />;
}

// Map product IDs to their demo components
const demoComponentMap: Record<string, React.ComponentType<{ tab: number; product: ProductDetail }>> = {
  identity: IdentityDemo,
  learn: LearnDemo,
  transit: TransitDemo,
  connect: ConnectDemo,
  campus: CampusDemo,
  intelligence: IntelligenceDemo,
  government: GovernmentDemo,
  pay: PayDemo,
};

// ════════════════════════════════════════════════════════════
// ECOSYSTEM ROLE — how this product connects to others
// ════════════════════════════════════════════════════════════
function ProductEcosystemRole({ product }: { product: ProductDetail }) {
  const others = products.filter((p) => p.id !== product.id);

  return (
    <section className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: product.color }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: product.color }}>
            Ecosystem Role
          </span>
        </div>
        <h2 className="font-display text-3xl font-medium tracking-tight text-[#1C1917] sm:text-4xl text-balance">
          How {product.shortName} connects everything.
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-[1.7] text-[#1C1917]/80">
          {product.shortName} doesn't operate in isolation. It shares a common identity, data model, and event bus with every other platform in the BRECCS ecosystem.
        </p>

        {/* Connection diagram */}
        <div className="mt-12 flex flex-col items-center">
          {/* Center node */}
          <div className="relative">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="grid h-20 w-20 place-items-center rounded-2xl shadow-lg"
              style={{ background: product.gradient }}
            >
              <product.icon className="h-10 w-10" accent="#fff" style={{ color: "#fff" }} />
            </motion.div>
            <motion.div
              className="absolute inset-0 rounded-2xl border-2"
              style={{ borderColor: `${product.color}30` }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </div>

          {/* Connecting lines + other products */}
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {others.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to="/ecosystem/$productId"
                  params={{ productId: p.id }}
                  className="group flex flex-col items-center gap-2 rounded-xl border p-4 transition-all hover:shadow-md"
                  style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}
                >
                  <div className="grid h-10 w-10 place-items-center rounded-lg transition-all" style={{ background: `${p.color}12` }}>
                    <p.icon className="h-5 w-5" accent={p.color} style={{ color: p.color }} />
                  </div>
                  <span className="text-xs font-medium text-[#1C1917]">{p.shortName}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// STATS
// ════════════════════════════════════════════════════════════
function ProductStats({ product }: { product: ProductDetail }) {
  return (
    <section className="relative overflow-hidden bg-[#1C1917] py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-0 h-72 w-72 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${product.color}15 0%, transparent 60%)` }} />
      </div>
      <div className="relative mx-auto max-w-5xl px-6 sm:px-8">
        <h2 className="text-center font-display text-3xl font-medium tracking-tight text-white sm:text-4xl">
          {product.shortName} at scale
        </h2>
        <div className="mt-12 grid grid-cols-2 gap-8 sm:grid-cols-4">
          {product.stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, ease: EASE }}
              className="text-center"
            >
              <div className="font-display text-3xl font-medium text-white sm:text-4xl" style={{ color: product.color }}>
                {s.value}
              </div>
              <div className="mt-2 text-xs font-medium uppercase tracking-wider text-white/40">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════
// NAVIGATION — prev/next product
// ════════════════════════════════════════════════════════════
function ProductNavigation({
  prev, next, onNavigate,
}: {
  prev: ProductDetail;
  next: ProductDetail;
  onNavigate: (id: string) => void;
}) {
  const PrevIcon = prev.icon;
  const NextIcon = next.icon;

  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            onClick={() => onNavigate(prev.id)}
            className="group flex items-center gap-4 rounded-2xl border p-6 text-left transition-all hover:shadow-lg"
            style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}
          >
            <ArrowRightIcon className="h-5 w-5 rotate-180 text-[#999] transition group-hover:text-[#1C1917]" accent="#999" style={{ color: "#999" }} />
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ background: `${prev.color}12` }}>
              <PrevIcon className="h-6 w-6" accent={prev.color} style={{ color: prev.color }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#999]">Previous</p>
              <p className="font-display text-lg font-medium text-[#1C1917]">{prev.name}</p>
            </div>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            onClick={() => onNavigate(next.id)}
            className="group flex items-center gap-4 rounded-2xl border p-6 text-right transition-all hover:shadow-lg"
            style={{ borderColor: "rgba(26,26,26,0.06)", background: "#fff" }}
          >
            <div className="ml-auto">
              <p className="text-[10px] uppercase tracking-wider text-[#999]">Next</p>
              <p className="font-display text-lg font-medium text-[#1C1917]">{next.name}</p>
            </div>
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ background: `${next.color}12` }}>
              <NextIcon className="h-6 w-6" accent={next.color} style={{ color: next.color }} />
            </div>
            <ArrowRightIcon className="h-5 w-5 text-[#999] transition group-hover:text-[#1C1917]" accent="#999" style={{ color: "#999" }} />
          </motion.button>
        </div>
      </div>
    </section>
  );
}

export default ProductDetailPage;
